import { HttpErrorResponse } from '@angular/common/http';

/**
 * Either a translation key or the server's own wording, shown as-is. `what`
 * is the key for what failed ("submissions"), filled into `{{what}}`.
 */
export type ErrorMessage = { key: string; what?: string } | { text: string };

const MAX_SERVER_MESSAGE_LENGTH = 160;

// Status phrases and framework defaults that say nothing the toast doesn't.
const GENERIC_MESSAGES = new Set([
  'bad request',
  'conflict',
  'error',
  'forbidden',
  'internal server error',
  'invalid input',
  'not found',
  'server error',
  'something went wrong',
  'unknown error',
  'unprocessable entity',
  'validation error'
]);

/**
 * The server's message when it is short, plain text and specific enough to
 * show a user: `detail`, `errors[0].detail` or `message`.
 */
export function getUsableServerMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return;
  const { detail, errors, message } = body as {
    detail?: unknown;
    errors?: { detail?: unknown }[];
    message?: unknown;
  };
  const candidate = [
    detail,
    Array.isArray(errors) ? errors[0]?.detail : undefined,
    message
  ].find((value): value is string => typeof value === 'string');
  const text = candidate?.trim();
  if (
    !text ||
    text.length > MAX_SERVER_MESSAGE_LENGTH ||
    /[<\n\r]/.test(text) ||
    GENERIC_MESSAGES.has(text.toLowerCase().replace(/[.!]+$/, ''))
  ) {
    return;
  }
  return text;
}

// Plot queries take their filters as a POST body; a failure there is a load
// that didn't happen, not an action that didn't go through.
const POST_QUERY_ENDPOINTS = [
  /\/v2\/plantations\/$/,
  /\/v2\/statistics\//,
  /\/v2\/layers\/tile-urls$/
];

export function isReadRequest(method: string, url: string): boolean {
  const verb = method.toUpperCase();
  if (verb === 'GET' || verb === 'HEAD') return true;
  const path = url.replace(/[?#].*$/s, '');
  return (
    verb === 'POST' && POST_QUERY_ENDPOINTS.some((query) => query.test(path))
  );
}

/**
 * What people call the data behind each endpoint, so a failure can say
 * "Couldn't load submissions" instead of a line that names nothing.
 * Checked in order, so a narrower path goes before any path it is under.
 */
const ENDPOINT_NAMES: readonly { path: RegExp; what: string }[] = [
  { path: /\/submission-candidates/, what: 'PLOTS_TO_SUBMIT' },
  { path: /\/submissions/, what: 'SUBMISSIONS' },
  { path: /\/import-review/, what: 'PLOTS_TO_REVIEW' },
  { path: /\/plantations\/evidence/, what: 'EVIDENCE' },
  { path: /\/plantations\/mitigations?/, what: 'MITIGATIONS' },
  { path: /\/plantations\/reviews?/, what: 'PLOT_REVIEWS' },
  { path: /\/plantations\/history/, what: 'PLOT_HISTORY' },
  { path: /\/plantations\/filters/, what: 'FILTER_OPTIONS' },
  { path: /\/filters\/favorites/, what: 'SAVED_FILTERS' },
  { path: /\/statistics\//, what: 'SUMMARY' },
  { path: /\/layers\//, what: 'MAP_LAYERS' },
  { path: /\/(datasets\/risks|risks\/)/, what: 'RISK_SETTINGS' },
  { path: /\/survey\//, what: 'QUESTIONNAIRES' },
  { path: /\/traceability-reports\//, what: 'REPORTS' },
  { path: /\/settings/, what: 'SETTINGS' },
  { path: /\/(plantations|agtrace\/plantation)/, what: 'PLOTS' }
];

const DOWNLOAD_ENDPOINT = /\/download/;

/** The `ERRORS.WHAT` key for the data a request was for, if it's one we name. */
export function getEndpointName(url: string | null): string | undefined {
  if (!url) return;
  const path = url.replace(/[?#].*$/s, '');
  const entry = ENDPOINT_NAMES.find((name) => name.path.test(path));
  return entry && `ERRORS.WHAT.${entry.what}`;
}

/** One line for a failed request: what happened, then what to do. */
export function getHttpErrorMessage(
  error: HttpErrorResponse,
  isRead: boolean
): ErrorMessage {
  switch (error.status) {
    case 0:
      return { key: 'ERRORS.OFFLINE' };
    case 403:
      return { key: 'ERRORS.FORBIDDEN' };
    case 404:
      return { key: 'ERRORS.NOT_FOUND' };
    case 400:
    case 409:
    case 422: {
      const text = getUsableServerMessage(error.error);
      return text ? { text } : { key: 'ERRORS.REJECTED' };
    }
    case 429:
      return { key: 'ERRORS.TOO_MANY_REQUESTS' };
    default: {
      if (DOWNLOAD_ENDPOINT.test(error.url?.replace(/[?#].*$/s, '') ?? '')) {
        return { key: 'ERRORS.DOWNLOAD_FAILED' };
      }
      const what = getEndpointName(error.url);
      if (what) {
        return {
          key: isRead ? 'ERRORS.LOAD_FAILED_WHAT' : 'ERRORS.ACTION_FAILED_WHAT',
          what
        };
      }
      return isRead
        ? { key: 'ERRORS.LOAD_FAILED' }
        : { key: 'ERRORS.ACTION_FAILED' };
    }
  }
}
